---
title: "Przegląd roczny budynku — co obejmuje i jak go przeprowadzić"
description: "Przegląd roczny budynku co obejmuje — praktyczny zakres obowiązkowej kontroli rocznej, krok po kroku od przygotowania do protokołu, z uwzględnieniem dokumentacji terenowej."
date: "2026-06-07"
slug: "przeglad-roczny-budynku-krok-po-kroku"
faq:
  - q: "Co obejmuje przegląd roczny budynku, a co nie?"
    a: "Kontrola roczna obejmuje elementy zewnętrzne narażone na czynniki atmosferyczne, instalację gazową, przewody kominowe i instalację elektryczną (w zakresie bezpieczeństwa pożarowego). Nie obejmuje pełnego przeglądu instalacji elektrycznej (to zakres pięcioletni) ani kompleksowej oceny estetyki."
  - q: "Do kiedy należy przeprowadzić kontrolę roczną?"
    a: "Prawo nie określa dokładnej daty dla obiektów poniżej 2000 m² zabudowy. Branżową praktyką jest termin do końca maja. Dla obiektów powyżej 2000 m² — pierwsze badanie do 31 maja, drugie do 30 listopada."
  - q: "Czy można samemu przeprowadzić kontrolę roczną?"
    a: "Nie — kontrolę musi przeprowadzić osoba z uprawnieniami budowlanymi w odpowiedniej specjalności. Właściciel lub zarządca zleca ją zewnętrznemu inspektorowi."
  - q: "Ile kosztuje kontrola roczna budynku?"
    a: "Dla typowego budynku wielorodzinnego do 10 lokali ceny wahają się od kilkuset do ponad tysiąca złotych. Cena obejmuje przeprowadzenie inspekcji i sporządzenie protokołu."
  - q: "Jak przechowywać protokół z kontroli rocznej?"
    a: "Protokół wpisuje się do KOB i przechowuje przez cały okres eksploatacji budynku. Kopie należy przechowywać minimum 5 lat."
---

Przegląd roczny budynku co obejmuje — to pytanie, które pojawia się na początku każdego sezonu inspekcyjnego. Kontrola roczna jest węższa niż pełna kontrola pięcioletnia, ale wymaga starannej dokumentacji i musi być przeprowadzona przez osobę z uprawnieniami. Ten poradnik prowadzi przez cały proces krok po kroku.

> **Uwaga:** Ten artykuł opisuje obowiązkową kontrolę roczną budynku. Jeśli szukasz szerszego omówienia wszystkich rodzajów przeglądów (roczne, pięcioletnie, dwukrotne), przeczytaj: [Przeglądy okresowe budynku — terminy, kary i kto może je wykonać](/blog/przeglady-okresowe-budynku-terminy-kary).

---

## Czym jest kontrola roczna i kto ją przeprowadza?

Kontrola roczna to **coroczne badanie stanu technicznego** elementów budynku narażonych na szkodliwe wpływy atmosferyczne, niszczące działanie czynników zewnętrznych i intensywną eksploatację. Jest to obowiązkowy przegląd okresowy wynikający z przepisów Prawa budowlanego.

Kontrolę może przeprowadzić wyłącznie **osoba posiadająca uprawnienia budowlane** w odpowiedniej specjalności. W zakresie przewodów kominowych dopuszcza się mistrza kominiarskiego.

---

## Zakres kontroli rocznej — co obejmuje?

### 1. Elementy zewnętrzne budynku

- **Elewacje** — pęknięcia, odpryski tynku, zawilgocenia, wykwity solne, uszkodzenia okładzin.
- **Dach i obróbki blacharskie** — stan pokrycia, szczelność obróbek, rynny i rury spustowe, kominki wentylacyjne.
- **Fundamenty i mury oporowe** — widoczne zarysowania, osiadanie, podmycia.
- **Tarasowane i drogi wewnętrzne** — stan nawierzchni, odprowadzenie wód opadowych.

### 2. Instalacje i urządzenia techniczne

- **Instalacja gazowa** — szczelność połączeń, stan kurków i zaworów, wentylacja pomieszczeń z gazem.
- **Przewody kominowe** — spalinowe, dymowe i wentylacyjne (drożność, stan techniczny, szczelność).
- **Instalacja elektryczna i odgromowa** — w zakresie bezpieczeństwa pożarowego (stan przewodów w przestrzeniach ogólnodostępnych, połączenia wyrównawcze, instalacja odgromowa).
- **Urządzenia do odśnieżania i usuwania lodu** — ogrzewanie rynien, czujniki temperatury.

### 3. Elementy wewnętrzne i wspólne

- **Klatki schodowe** — stan stopni, balustrad, pochwytów, oświetlenia awaryjnego.
- **Piwnice i poddasza** — zawilgocenia, szczelność izolacji, drogi ewakuacyjne.
- **Kotłownie i maszynownie** — stan ogólny, wentylacja, oznakowanie.

### 4. Elementy bezpieczeństwa pożarowego

- stan dróg ewakuacyjnych i oznaczeń,
- sprawność klap dymowych i drzwi przeciwpożarowych (w zakresie widocznym),
- dostępność gaśnic i hydrantów (kontrola wzrokowa).

---

## Kontrola roczna a kontrola pięcioletnia — różnice

| Element | Kontrola roczna | Kontrola pięcioletnia |
|---|---|---|
| Częstotliwość | Co roku (2× dla obiektów >2000 m²) | Co 5 lat |
| Instalacja elektryczna | Bezpieczeństwo pożarowe | Pełna szczegółowa |
| Zakres instalacji | Gaz + kominy | Wszystkie instalacje |
| Estetyka | Nie wymagana | Wymagana |
| Budowle pomocnicze | Wybiórczo | Kompleksowo |

---

## Jak przeprowadzić kontrolę roczną krok po kroku?

### Krok 1 — Przygotowanie

- Pobierz lub przygotuj formularz kontroli rocznej z listą wszystkich elementów podlegających sprawdzeniu.
- Zapoznaj się z protokołem z poprzedniej kontroli — sprawdź, czy zalecenia zostały wykonane.
- Przygotuj plan kondygnacji i elewacji budynku.

### Krok 2 — Inspekcja zewnętrzna

Zacznij od zewnątrz — obedrzyj budynek dookoła, sprawdzając elewacje, dach (z bezpiecznej odległości lub wejście przez klapę dachową), rynny i spusty. Każdą stwierdzoną usterkę fotografuj, notując lokalizację.

### Krok 3 — Inspekcja wewnętrzna

Przejdź przez klatki schodowe, piwnice, poddasze i pomieszczenia techniczne. Sprawdź instalacje w zakresie wymaganym przez kontrolę roczną.

### Krok 4 — Dokumentacja

Każda usterka powinna być opisana z:
- **lokalizacją** (kondygnacja, element, strona świata),
- **charakterem wady** (pęknięcie, korozja, brak szczelności),
- **oceną pilności** (obserwacja / do naprawy / pilna naprawa),
- **zdjęciem z datą i GPS**.

### Krok 5 — Protokół i wpis do KOB

Sporządź protokół zawierający wszystkie wymagane dane i zaleceń. Wpisz wyniki do **książki obiektu budowlanego (KOB)** lub — dla nowych budynków — prześlij dane do c-KOB GUNB. Przekaż kopię właścicielowi lub zarządcy.

---

## Najczęstsze usterki wykrywane podczas kontroli rocznej

- Odpryski tynku elewacyjnego i zarysowania pod oknami.
- Nieszczelne lub zatkane rynny i rury spustowe.
- Uszkodzone obróbki blacharskie i kominy.
- Zawilgocone mury piwnic.
- Zużyte uszczelnienia okien w przestrzeniach wspólnych.
- Luźne lub brakujące elementy balustrady na klatce schodowej.
- Niedrożne kanały wentylacyjne.

---

## Jak Pinveo wspiera kontrolę roczną?

Pinveo eliminuje papierowe notatki i przyspiesza każdy etap:

- **Formularz rocznej inspekcji** — gotowy szablon z listą wszystkich wymaganych elementów, dostosowany do obowiązkowej kontroli rocznej.
- **Markery na rzucie PDF** — inspektor dotyka miejsca na planie i dodaje opis + zdjęcie — lokalizacja jest jednoznaczna.
- **Zdjęcia z GPS i znacznikiem czasu** — dane geolokalizacyjne dołączane automatycznie do każdego zdjęcia.
- **Raport PDF** — gotowy protokół z podpisem i zdjęciami wygenerowany po zakończeniu inspekcji.
- **Praca offline** — brak zasięgu w piwnicy lub na dachu nie zatrzymuje dokumentowania.

Więcej o przeglądach technicznych z Pinveo: [Przeglądy okresowe budynków](/zastosowania/przeglady-okresowe-budynkow).

---

## Podsumowanie

Kontrola roczna budynku obejmuje przede wszystkim elementy zewnętrzne narażone na czynniki atmosferyczne, instalacje gazowe, przewody kominowe i instalację elektryczną w zakresie bezpieczeństwa pożarowego. Dokładna dokumentacja fotograficzna i wpis do KOB to obowiązkowe elementy. Pinveo skraca czas inspekcji i zapewnia kompletną dokumentację terenową.

---

## FAQ

### Co obejmuje przegląd roczny budynku, a co nie?

Kontrola roczna obejmuje elementy zewnętrzne narażone na czynniki atmosferyczne, instalację gazową, przewody kominowe i instalację elektryczną (w zakresie bezpieczeństwa pożarowego). Nie obejmuje pełnego przeglądu instalacji elektrycznej (to zakres pięcioletni) ani kompleksowej oceny estetyki.

### Do kiedy należy przeprowadzić kontrolę roczną?

Prawo nie określa dokładnej daty dla obiektów poniżej 2000 m² zabudowy. Branżową praktyką jest termin do końca maja. Dla obiektów powyżej 2000 m² — pierwsze badanie do 31 maja, drugie do 30 listopada.

### Czy można samemu przeprowadzić kontrolę roczną?

Nie — kontrolę musi przeprowadzić osoba z uprawnieniami budowlanymi w odpowiedniej specjalności. Właściciel lub zarządca zleca ją zewnętrznemu inspektorowi.

### Ile kosztuje kontrola roczna budynku?

Koszt zależy od wielkości obiektu i zakresu. Dla typowego budynku wielorodzinnego do 10 lokali ceny wahają się od kilkuset do ponad tysiąca złotych. Cena obejmuje przeprowadzenie inspekcji i sporządzenie protokołu.

### Jak przechowywać protokół z kontroli rocznej?

Protokół wpisuje się do KOB i przechowuje przez cały okres eksploatacji budynku. Kopie należy przechowywać minimum 5 lat.
